import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { OneRosterApiTyped } from '@/lib/http.server';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserHasRole } from '@/middlewares/userAuthMiddleware';

import { Enrollment } from '../types';

export const getEnrollmentsDefaultParams = {
  filter: `status='active'`,
  sort: 'beginDate',
  orderBy: 'desc',
  limit: '100',
  offset: '0',
  search: '',
};

type EnrollmentsResponse = {
  enrollments: Enrollment[];
  totalCount: number;
  pageCount: number;
  pageNumber: number;
  offset: number;
  limit: number;
};

const fetchOnerosterEnrollments = async (
  onerosterApi: OneRosterApiTyped,
  params: {
    sort?: string;
    limit?: string;
    offset?: string;
    orderBy?: string;
    search?: string;
    filter?: string;
  }
) => {
  const mergedParams = Object.assign({}, getEnrollmentsDefaultParams, params);

  // Create filter from search term if provided
  let finalFilter = mergedParams.filter;
  if (mergedParams.search && mergedParams.search.trim()) {
    const searchTerm = mergedParams.search.trim();
    // Create a filter that searches across user, course, school name
    const searchFilters = [
      `user~'${searchTerm}'`,
      `course~'${searchTerm}'`,
      `school~'${searchTerm}'`,
    ];
    finalFilter = `(${finalFilter}) AND (${searchFilters.join(' OR ')})`;
  }

  const response = await onerosterApi.GET(
    '/ims/oneroster/rostering/v1p2/enrollments/',
    {
      params: {
        query: {
          filter: finalFilter,
          sort: mergedParams.sort,
          orderBy: (mergedParams.orderBy ?? 'desc') as 'asc' | 'desc',
          limit: parseInt(mergedParams.limit),
          offset: parseInt(mergedParams.offset),
          search: mergedParams.search ?? '',
        },
      },
    }
  );

  const transformedData = {
    ...response.data,
    enrollments: response.data?.enrollments || [],
  };

  return transformedData;
};

type GetEnrollmentsResponse = {
  data: EnrollmentsResponse | null;
  success: boolean;
  error: string | null;
};

export const getEnrollments = createServerFn({ method: 'POST' })
  .middleware([standardMiddlewares])
  .validator(
    z.object({
      sort: z.string().optional().default(getEnrollmentsDefaultParams.sort),
      limit: z.string().optional().default(getEnrollmentsDefaultParams.limit),
      offset: z.string().optional().default(getEnrollmentsDefaultParams.offset),
      orderBy: z
        .string()
        .optional()
        .default(getEnrollmentsDefaultParams.orderBy),
      search: z.string().optional().default(getEnrollmentsDefaultParams.search),
      filter: z.string().optional().default(getEnrollmentsDefaultParams.filter),
    })
  )
  .handler(async ({ context: { oneRosterApiTyped, cognitoUser }, data }) => {
    await assertUserHasRole(cognitoUser, oneRosterApiTyped, ELEVATED_ROLES);
    const response = await fetchOnerosterEnrollments(oneRosterApiTyped, data);
    return {
      data: response,
      success: true,
      error: null,
    } as GetEnrollmentsResponse;
  });
